/**
 * useSipClient — SIP-клиент (JsSIP + Zadarma JSONP credentials)
 *
 * Flow:
 * 1. Бэкенд: GET /api/v1/telephony/webrtc/key → { key, sipId }
 * 2. JSONP: get_sips.php + get_webphone_data.php → { domain, username, pass }
 * 3. JsSIP: wss://{domain}:4443, регистрация с временными credentials
 *
 * КРИТИЧНО: JsSIP auto-reconnect ОТКЛЮЧЁН.
 * Zadarma инвалидирует пароль при разрыве WS. JsSIP auto-reconnect отправляет
 * REGISTER со старым паролем → 401 → Zadarma блокирует extension → даже свежие
 * credentials получают 401. Поэтому при разрыве WS мы уничтожаем UA, ждём 10с
 * (cooldown на стороне Zadarma), и проходим полный JSONP цикл.
 */
import { ref, computed } from 'vue'
import JsSIP from 'jssip'
import { UA, WebSocketInterface } from 'jssip'
import type { UAConfiguration, IncomingRTCSessionEvent } from 'jssip/lib/UA'
import type { RTCSession } from 'jssip/lib/RTCSession'
import { telephonyApi } from '@/features/telephony/api/telephony.api'
import { useTelephonyStore } from '@/features/telephony/stores/telephony.store'
import { isImpersonating } from '@/features/auth/tokenStorage'

export type SipStatus = 'disconnected' | 'connecting' | 'registered' | 'error'

let uaInstance: UA | null = null
let currentSession: RTCSession | null = null
let currentDomain: string | null = null
let storedKey: string | null = null
let storedSipId: string | null = null

let reconnectTimer: ReturnType<typeof setTimeout> | null = null
let reconnectAttempts = 0
let lastConnectTime = 0

const RECONNECT_COOLDOWN_MS = 3000
const MAX_RECONNECT_ATTEMPTS = 10
// 10с начальная задержка — даём Zadarma сбросить блокировку extension после 401
const INITIAL_RECONNECT_DELAY_MS = 10_000
const MAX_RECONNECT_DELAY_MS = 60_000

const SIP_WSS_PORT = 4443
const ZADARMA_WEBRTC_BASE = 'https://api.zadarma.com/sys/webrtc'

const status = ref<SipStatus>('disconnected')
const error = ref<string | null>(null)
const isBusy = ref(false)
const isRegistered = computed(() => status.value === 'registered')

// --- JSONP ---

interface WebPhoneData {
  domain: string
  username: string
  pass: string
}

interface ZdrmWebPhone {
  zadarmaGetSipsCallback?: (data: Record<string, unknown>) => void
  zadarmaCallbackAnswer?: (data: Record<string, unknown>) => void
  zadarmaCallbackCall?: (data: Record<string, unknown>) => void
}

declare global {
  interface Window {
    zdrmWebPhone?: ZdrmWebPhone
  }
}

function ensureZdrmWebPhone(): ZdrmWebPhone {
  if (!window.zdrmWebPhone) {
    window.zdrmWebPhone = {}
  }
  return window.zdrmWebPhone
}

function zadarmaJsonp<T>(
  url: string,
  callbackPath: string,
  callbackSetter: (zdrm: ZdrmWebPhone, resolve: (data: T) => void) => void,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const zdrm = ensureZdrmWebPhone()
    const timeout = setTimeout(() => {
      reject(new Error(`JSONP timeout: ${url}`))
      script.remove()
    }, 10000)

    callbackSetter(zdrm, (data: T) => {
      clearTimeout(timeout)
      script.remove()
      resolve(data)
    })

    const script = document.createElement('script')
    script.src = `${url}&jsonpCallback=${encodeURIComponent(callbackPath)}&_t=${Date.now()}`
    script.onerror = () => {
      clearTimeout(timeout)
      script.remove()
      reject(new Error(`JSONP script error: ${url}`))
    }
    document.head.appendChild(script)
  })
}

// --- SIP Client ---

export function useSipClient() {
  function ensureAudioElement(): HTMLAudioElement {
    let el = document.getElementById('sip-remote-audio') as HTMLAudioElement | null
    if (!el) {
      el = document.createElement('audio')
      el.id = 'sip-remote-audio'
      el.autoplay = true
      el.style.display = 'none'
      document.body.appendChild(el)
    }
    return el
  }

  function attachStream(session: RTCSession) {
    const audio = ensureAudioElement()
    const pc = session.connection
    if (!pc) return

    if (pc.getReceivers().length > 0) {
      const track = pc.getReceivers()[0]?.track
      if (track) {
        const stream = new MediaStream()
        stream.addTrack(track)
        audio.srcObject = stream
        audio.play().catch((e) => console.warn('[SIP] audio play error:', e))
      }
    }

    pc.ontrack = (event: RTCTrackEvent) => {
      if (event.streams?.[0]) {
        audio.srcObject = event.streams[0]
        audio.play().catch((e) => console.warn('[SIP] audio play error:', e))
      }
    }
  }

  /** Уничтожить UA без побочных эффектов (не запускает reconnect). */
  function destroyUA() {
    if (uaInstance) {
      try {
        uaInstance.removeAllListeners()
        uaInstance.stop()
      } catch {
        // ignore
      }
      uaInstance = null
    }
  }

  function scheduleReconnect() {
    if (reconnectTimer) return

    if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      console.error('[SIP] Max reconnect attempts reached, giving up')
      status.value = 'error'
      error.value = 'Не удалось восстановить SIP-соединение'
      reconnectAttempts = 0
      return
    }

    const backoff = Math.min(
      INITIAL_RECONNECT_DELAY_MS * Math.pow(2, reconnectAttempts),
      MAX_RECONNECT_DELAY_MS,
    )
    reconnectAttempts++

    console.info(
      `[SIP] Reconnect in ${backoff}ms (attempt ${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})`,
    )
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null
      status.value = 'disconnected'
      connect()
        .then((ok) => {
          if (ok) {
            reconnectAttempts = 0
          } else {
            scheduleReconnect()
          }
        })
        .catch(() => {
          scheduleReconnect()
        })
    }, backoff)
  }

  function handleNewSession(session: RTCSession) {
    const store = useTelephonyStore()

    if (isBusy.value && currentSession) {
      console.warn('[SIP] Already in a call, rejecting new session')
      session.terminate({ status_code: 486 })
      return
    }

    currentSession = session
    isBusy.value = true

    const remoteUser = session.remote_identity?.uri?.user || ''
    const direction = session.direction === 'incoming' ? 'inbound' : 'outbound'
    console.info('[SIP] New session:', direction, remoteUser)

    store.handleCallEvent({
      type: 'CALL_RINGING',
      callId: session.id || '',
      pbxCallId: '',
      managerId: '',
      destination: direction === 'outbound' ? remoteUser : undefined,
      callerId: direction === 'inbound' ? remoteUser : undefined,
    })

    session.on('ended', () => {
      console.info('[SIP] Call ended')
      isBusy.value = false
      currentSession = null
      store.handleCallEvent({
        type: 'CALL_ENDED',
        callId: session.id || '',
        pbxCallId: '',
        managerId: '',
        disposition: 'answered',
      })
    })

    session.on('failed', (e: { cause?: string }) => {
      console.warn('[SIP] Call failed:', e.cause)
      isBusy.value = false
      currentSession = null
      store.handleCallEvent({
        type: 'CALL_ENDED',
        callId: session.id || '',
        pbxCallId: '',
        managerId: '',
        disposition: e.cause || 'failed',
      })
    })

    session.on('confirmed', () => {
      console.info('[SIP] Call confirmed')
      attachStream(session)
    })

    session.on('progress', () => {
      console.info('[SIP] Call progress (ringing)')
    })

    session.on('accepted', () => {
      console.info('[SIP] Call accepted')
      attachStream(session)
      store.handleCallEvent({
        type: 'CALL_ANSWERED',
        callId: session.id || '',
        pbxCallId: '',
        managerId: '',
      })
    })

    if (session.direction === 'incoming') {
      console.info('[SIP] Auto-answering incoming call')
      session.answer({
        mediaConstraints: { audio: true, video: false },
      })
    }
  }

  // --- JSONP helpers ---

  async function initZadarmaSips(key: string, sipId: string): Promise<void> {
    const url =
      `${ZADARMA_WEBRTC_BASE}/get_sips.php?` +
      `key=${encodeURIComponent(key)}&integrationType=site&sip=${encodeURIComponent(sipId)}`
    try {
      await zadarmaJsonp<Record<string, unknown>>(
        url,
        'window.zdrmWebPhone.zadarmaGetSipsCallback',
        (zdrm, resolve) => {
          zdrm.zadarmaGetSipsCallback = resolve
        },
      )
    } catch (e) {
      console.warn('[SIP] get_sips.php failed (non-critical):', e)
    }
  }

  async function fetchWebPhoneData(key: string, sipId: string): Promise<WebPhoneData> {
    const url =
      `${ZADARMA_WEBRTC_BASE}/get_webphone_data.php?` +
      `integrationType=site&key=${encodeURIComponent(key)}&sipId=${encodeURIComponent(sipId)}`
    const result = await zadarmaJsonp<Record<string, unknown>>(
      url,
      'window.zdrmWebPhone.zadarmaCallbackAnswer',
      (zdrm, resolve) => {
        zdrm.zadarmaCallbackAnswer = resolve
      },
    )
    const wpData = result as unknown as WebPhoneData
    if (!wpData.domain || !wpData.username || !wpData.pass) {
      throw new Error(`get_webphone_data.php error: ${JSON.stringify(result)}`)
    }
    return wpData
  }

  // --- UA ---

  function createUA(domain: string, username: string, password: string): Promise<void> {
    return new Promise((resolve, reject) => {
      destroyUA()

      const wssUrl = `wss://${domain}:${SIP_WSS_PORT}`
      const socket = new WebSocketInterface(wssUrl)

      ;(JsSIP.C as Record<string, unknown>).USER_AGENT = 'Zadarma webRTC'

      const config: UAConfiguration = {
        sockets: [socket],
        uri: `sip:${username}@${domain}`,
        authorization_user: username,
        password: password,
        display_name: username,
        register: true,
        register_expires: 580,
        session_timers: false,
        // Auto-reconnect ОТКЛЮЧЁН. При разрыве WS Zadarma инвалидирует пароль.
        // JsSIP auto-reconnect отправит REGISTER со старым паролем → 401 →
        // extension блокируется → даже свежие credentials получают 401.
        connection_recovery_min_interval: 99999,
        connection_recovery_max_interval: 99999,
      }

      console.info('[SIP] Creating UA →', wssUrl)
      const ua = new UA(config)

      let resolved = false

      ua.on('registered', () => {
        console.info('[SIP] Registered on', domain)
        status.value = 'registered'
        error.value = null
        if (!resolved) {
          resolved = true
          resolve()
        }
      })

      ua.on('registrationFailed', (e: { cause?: string; response?: { status_code?: number } }) => {
        console.error('[SIP] Registration failed:', e.cause, e.response?.status_code)
        if (!resolved) {
          resolved = true
          reject(new Error(`SIP registration failed: ${e.cause}`))
        }
        // Не запускаем reconnect отсюда — connect() сам обработает ошибку
      })

      ua.on('unregistered', () => {
        console.warn('[SIP] Unregistered')
      })

      ua.on('newRTCSession', (ev: IncomingRTCSessionEvent) => {
        handleNewSession(ev.session)
      })

      ua.on('connected', () => {
        console.info('[SIP] WebSocket connected to', wssUrl)
      })

      ua.on('disconnected', (e: { error?: boolean; code?: number; reason?: string }) => {
        console.warn('[SIP] WebSocket disconnected', {
          error: e?.error,
          code: e?.code,
          reason: e?.reason,
        })
        // Auto-reconnect отключён — WS не восстановится сам.
        // Уничтожаем UA и планируем полный JSONP цикл.
        status.value = 'connecting'
        destroyUA()
        scheduleReconnect()
      })

      ua.start()
      uaInstance = ua
    })
  }

  // --- Public API ---

  async function connect(): Promise<boolean> {
    // Вкладка просмотра не регистрирует SIP от имени пользователя: звонки шли бы
    // админу. Здесь, а не у вызывающих, — их несколько (виджет, звонок из карточки).
    if (isImpersonating()) return false

    if (uaInstance && status.value === 'registered') {
      return true
    }

    const now = Date.now()
    if (now - lastConnectTime < RECONNECT_COOLDOWN_MS) {
      return false
    }
    lastConnectTime = now

    destroyUA()
    status.value = 'connecting'
    error.value = null

    try {
      // 1. WebRTC key от бэкенда
      const creds = await telephonyApi.getWebRTCKey(window.location.hostname)
      if (!creds.key || !creds.sipId) {
        throw new Error('Бэкенд не вернул WebRTC key или SIP ID')
      }

      // 2. JSONP: init session
      await initZadarmaSips(creds.key, creds.sipId)

      // 3. JSONP: credentials
      const wpData = await fetchWebPhoneData(creds.key, creds.sipId)

      storedKey = creds.key
      storedSipId = creds.sipId
      currentDomain = wpData.domain

      console.info('[SIP] Connecting to', wpData.domain, 'as', wpData.username)

      // 4. JsSIP
      await createUA(wpData.domain, wpData.username, wpData.pass)

      reconnectAttempts = 0
      return true
    } catch (e) {
      status.value = 'error'
      error.value = e instanceof Error ? e.message : 'Ошибка подключения SIP'
      console.error('[SIP] connect error:', e)
      return false
    }
  }

  /**
   * Исходящий звонок.
   * 1. JSONP call_to — уведомление PBX о маршрутизации
   * 2. Обновление пароля UA (Zadarma ротирует при call_to)
   * 3. SIP INVITE
   */
  async function makeCall(phoneNumber: string): Promise<void> {
    if (!uaInstance || status.value !== 'registered') {
      throw new Error('SIP не зарегистрирован')
    }
    if (isBusy.value) {
      throw new Error('Уже в разговоре')
    }
    if (!currentDomain || !storedKey || !storedSipId) {
      throw new Error('SIP не инициализирован')
    }

    // JSONP call_to — PBX routing + получаем актуальный пароль
    const callToUrl =
      `${ZADARMA_WEBRTC_BASE}/get_webphone_data.php?` +
      `integrationType=site&key=${encodeURIComponent(storedKey)}` +
      `&sipId=${encodeURIComponent(storedSipId)}` +
      `&call_to=${encodeURIComponent(phoneNumber)}`

    const result = await zadarmaJsonp<Record<string, unknown>>(
      callToUrl,
      'window.zdrmWebPhone.zadarmaCallbackCall',
      (zdrm, resolve) => {
        zdrm.zadarmaCallbackCall = resolve
      },
    )

    const wpData = result as unknown as WebPhoneData

    // Zadarma может сменить домен и пароль при call_to
    if (wpData.domain && wpData.domain !== currentDomain) {
      // Домен сменился — нужен полный реконнект на новый сервер
      console.warn('[SIP] Domain changed on call_to:', currentDomain, '→', wpData.domain)
      currentDomain = wpData.domain
      await createUA(wpData.domain, wpData.username, wpData.pass)
      storedKey = storedKey // keep
      storedSipId = storedSipId // keep
    } else if (wpData.pass && uaInstance) {
      // Домен тот же — просто обновляем пароль
      ;(uaInstance as unknown as { _configuration: { password: string } })._configuration.password =
        wpData.pass
    }

    const target = `sip:${phoneNumber}@${currentDomain}`
    console.info('[SIP] Calling', target)

    uaInstance.call(target, {
      mediaConstraints: { audio: true, video: false },
      rtcOfferConstraints: { offerToReceiveAudio: true },
    })
  }

  function disconnect() {
    if (currentSession) {
      try {
        currentSession.terminate()
      } catch {
        // ignore
      }
      currentSession = null
      isBusy.value = false
    }
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
    destroyUA()
    currentDomain = null
    storedKey = null
    storedSipId = null
    reconnectAttempts = 0
    status.value = 'disconnected'
    error.value = null
  }

  function hangup() {
    if (currentSession) {
      try {
        currentSession.terminate()
      } catch {
        // ignore
      }
      currentSession = null
      isBusy.value = false
    }
  }

  return {
    status,
    error,
    isRegistered,
    isBusy,
    connect,
    disconnect,
    makeCall,
    hangup,
  }
}
